import { NavLink } from 'react-router-dom';
import {
  AlertTriangle,
  ClipboardCheck,
  KanbanSquare,
  Settings,
  BookOpen,
  LineChart,
  MessageSquare,
  X,
} from 'lucide-react';
import { useAuth, type CockpitRole } from '@/auth/AuthProvider';
import { useHealth } from '@/features/health/hooks';
import { cockpitVersion } from '@/version';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: typeof AlertTriangle;
  /** Roles allowed to see this menu entry. Omit to allow everyone. Must
   * stay in sync with the route-level RoleGate so the menu only shows
   * destinations the user can actually open. */
  readonly allowedRoles?: readonly CockpitRole[];
}

const navItems: readonly NavItem[] = [
  { to: '/queue', label: 'Queue', icon: AlertTriangle },
  { to: '/drafts', label: 'Drafts', icon: ClipboardCheck },
  { to: '/feedback', label: 'Feedback', icon: KanbanSquare },
  {
    to: '/admin',
    label: 'Admin',
    icon: Settings,
    allowedRoles: ['SO-Supervisor', 'SO-Admin'],
  },
];

function canSeeNavItem(item: NavItem, principalRoles: readonly CockpitRole[]): boolean {
  if (!item.allowedRoles || item.allowedRoles.length === 0) return true;
  return principalRoles.some((role) => item.allowedRoles!.includes(role));
}

/**
 * Foot-of-sidebar version block. The cockpit bundle is stamped with the
 * Azure Pipelines `BUILD_ID` (or the literal `local` for ad-hoc dev builds);
 * the backend's `/healthz` returns the same compound shape. We surface
 * cockpit + backend versions side-by-side and flag a mismatch so an operator
 * who is sitting on a stale bundle gets prompted to hard-reload.
 */
function SidebarVersion(): JSX.Element {
  const { data, isError } = useHealth();
  const backendCompound = data?.version ?? 'unreachable';
  const mismatch = data ? data.version !== cockpitVersion.compound : false;
  const status = isError
    ? 'Backend unreachable'
    : mismatch
      ? 'Version mismatch — hard reload'
      : 'Versions in sync';

  return (
    <div
      className={cn(
        'mt-auto border-t px-4 py-3 text-[11px] leading-snug text-muted-foreground',
        mismatch && 'bg-warning/10 text-warning-foreground',
      )}
      title={`${status} | Cockpit ${cockpitVersion.compound} | Backend ${backendCompound}`}
    >
      <div className="font-medium text-foreground/80">Cockpit</div>
      <div className="truncate">{cockpitVersion.compound}</div>
      <div className="mt-2 font-medium text-foreground/80">Backend</div>
      <div className="truncate">{backendCompound}</div>
      <div
        className={cn(
          'mt-2 truncate',
          mismatch ? 'text-warning-foreground' : isError ? 'text-destructive' : '',
        )}
      >
        {status}
      </div>
    </div>
  );
}

interface SidebarProps {
  onFeedbackClick?: () => void;
  onNavigate?: () => void;
  mobileOpen?: boolean;
}

export function Sidebar({
  onFeedbackClick,
  onNavigate,
  mobileOpen = false,
}: SidebarProps): JSX.Element {
  const { principal } = useAuth();
  const principalRoles = principal?.roles ?? [];
  const visibleItems = navItems.filter((item) => canSeeNavItem(item, principalRoles));
  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-50 flex w-56 flex-col border-r bg-card md:static md:flex md:h-full md:shrink-0',
        mobileOpen ? 'flex' : 'hidden',
      )}
    >
      <div className="flex items-center gap-2 px-4 py-4">
        <img
          src="/techmer-logo.png"
          alt="Techmer PM"
          className="h-8 w-8 shrink-0 rounded-md object-contain"
          width={32}
          height={32}
        />
        <div className="flex flex-col">
          <span className="text-sm font-semibold">Sales Order Cockpit</span>
          <span className="text-xs text-muted-foreground">Techmer</span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="ml-auto shrink-0 md:hidden"
          onClick={onNavigate}
          aria-label="Close navigation"
          title="Close navigation"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <nav className="flex-1 space-y-1 px-2">
        {visibleItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground',
                isActive &&
                  'bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground',
              )
            }
          >
            <Icon className="h-4 w-4" />
            <span>{label}</span>
          </NavLink>
        ))}
        <a
          href="/dashboard"
          onClick={onNavigate}
          target="_blank"
          rel="noreferrer"
          className="mt-4 flex items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <LineChart className="h-4 w-4" />
          <span>Dashboard</span>
        </a>
        <a
          href="/docs"
          onClick={onNavigate}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <BookOpen className="h-4 w-4" />
          <span>Docs</span>
        </a>
      </nav>
      {onFeedbackClick ? (
        <div className="border-t px-2 py-2">
          <Button
            type="button"
            variant="ghost"
            className="w-full justify-start px-3"
            onClick={onFeedbackClick}
          >
            <MessageSquare className="h-4 w-4" />
            <span>Send feedback</span>
          </Button>
        </div>
      ) : null}
      <SidebarVersion />
    </aside>
  );
}
